// ./src/components/generic/LocationPreview/LocationPreview.tsx
import React, { useMemo, useCallback } from "react";
import {
  Box,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Skeleton,
  useMantineTheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";

import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface LocationPreviewProps {
  /** Latitude coordinate */
  latitude: number;

  /** Longitude coordinate */
  longitude: number;

  /** Whether to show the static map image (default: true) */
  showMap?: boolean;

  /** Map zoom level (default: 15) */
  zoom?: number;

  /** Height of the map image in pixels (default: 120) */
  mapHeight?: number;

  /** Whether to show the coordinates text below the map (default: true) */
  showCoordinates?: boolean;

  /** Optional label displayed above the map */
  label?: string;

  /** Whether the component is in a loading state */
  loading?: boolean;

  /** Coordinate decimal precision (default: 5) */
  precision?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_ZOOM = 15;
const DEFAULT_MAP_HEIGHT = 120;
const DEFAULT_PRECISION = 5;
const STATIC_MAP_WIDTH = 600; // Request a wide image, CSS will handle display
const STATIC_MAP_HEIGHT = 200; // Slightly taller than display for retina/quality

// ============================================
// Helpers
// ============================================

/**
 * Build Google Maps Static API URL
 */
const buildStaticMapUrl = (
  latitude: number,
  longitude: number,
  zoom: number,
  colorScheme: string
): string => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const marker = `color:red|${latitude},${longitude}`;

  // Use dark map style for dark mode
  const styleParams =
    colorScheme === "dark"
      ? "&style=feature:all|element:geometry|color:0x242f3e" +
      "&style=feature:all|element:labels.text.stroke|color:0x242f3e" +
      "&style=feature:all|element:labels.text.fill|color:0x746855" +
      "&style=feature:water|element:geometry|color:0x17263c" +
      "&style=feature:road|element:geometry|color:0x38414e" +
      "&style=feature:road|element:geometry.stroke|color:0x212a37"
      : "";

  return (
    `https://maps.googleapis.com/maps/api/staticmap` +
    `?center=${latitude},${longitude}` +
    `&zoom=${zoom}` +
    `&size=${STATIC_MAP_WIDTH}x${STATIC_MAP_HEIGHT}` +
    `&scale=2` +
    `&markers=${encodeURIComponent(marker)}` +
    `${styleParams}` +
    `&key=${apiKey}`
  );
};

/**
 * Build a universal "open in maps" URL.
 * google.com/maps links work cross-platform and redirect
 * to Apple Maps on iOS if that is the user's preference.
 */
const buildMapsUrl = (latitude: number, longitude: number): string => {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

// ============================================
// Component
// ============================================

export const LocationPreview: React.FC<LocationPreviewProps> = ({
  latitude,
  longitude,
  showMap = true,
  zoom = DEFAULT_ZOOM,
  mapHeight = DEFAULT_MAP_HEIGHT,
  showCoordinates = true,
  label,
  loading = false,
  precision = DEFAULT_PRECISION,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation(["common"]);
  const clipboard = useClipboard({ timeout: 1500 });

  const mapsUrl = useMemo(
    () => buildMapsUrl(latitude, longitude),
    [latitude, longitude]
  );

  const staticMapUrl = useMemo(
    () => buildStaticMapUrl(latitude, longitude, zoom, colorScheme),
    [latitude, longitude, zoom, colorScheme]
  );

  const formattedCoords = useMemo(
    () => `${latitude.toFixed(precision)}, ${longitude.toFixed(precision)}`,
    [latitude, longitude, precision]
  );

  const handleOpenMaps = useCallback(() => {
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  }, [mapsUrl]);

  const handleCopyCoords = useCallback(() => {
    clipboard.copy(formattedCoords);
  }, [clipboard, formattedCoords]);

  // ----------------------------------------
  // Loading state
  // ----------------------------------------
  if (loading) {
    return (
      <Box>
        {showMap && (
          <Skeleton
            height={mapHeight}
            radius="sm"
            mb={showCoordinates ? 6 : 0}
          />
        )}
        {showCoordinates && <Skeleton height={16} width="60%" radius="sm" />}
      </Box>
    );
  }

  return (
    <Box>
      {/* Optional label */}
      {label && (
        <Text size="xs" fw={500} mb={4}>
          {label}
        </Text>
      )}

      {/* Static Map Image (non-clickable) with Open button */}
      {showMap && (
        <Box
          style={{
            width: "100%",
            height: mapHeight,
            overflow: "hidden",
            borderRadius: theme.radius.sm,
            border: `1px solid var(--mantine-color-default-border)`,
            position: "relative",
          }}
        >
          <img
            src={staticMapUrl}
            alt={t("common:location.mapPreviewAlt", {
              defaultValue: "Location preview map",
            })}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            loading="lazy"
          />

          {/* Open in Maps button — top-right corner */}
          <Tooltip
            label={t("common:location.openInMaps", {
              defaultValue: "Open in Google Maps",
            })}
            position="left"
            withArrow
            openDelay={300}
          >
            <ActionIcon
              variant="white"
              size="sm"
              onClick={handleOpenMaps}
              aria-label={t("common:location.openInMaps", {
                defaultValue: "Open in Google Maps",
              })}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
              }}
            >
              <AppIcon iconName="ExternalLink" size={14} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}

      {/* Coordinates row with copy button */}
      {showCoordinates && (
        <Group
          gap="xs"
          mt={showMap ? 6 : 0}
          wrap="nowrap"
        >
          <AppIcon
            iconName="MapPin"
            size={14}
            color="var(--mantine-color-green-6)"
            style={{ flexShrink: 0 }}
          />
          <Text size="xs" c="dimmed" style={{ flexGrow: 1 }}>
            {t("common:location.coordinatesCaptured", {
              defaultValue: "Location captured",
            })}
            {" · "}
            <Text span size="xs" c="dimmed" ff="monospace">
              {formattedCoords}
            </Text>
          </Text>

          {/* Copy coordinates button */}
          <Tooltip
            label={
              clipboard.copied
                ? t("common:actions.copied", { defaultValue: "Copied!" })
                : t("common:location.copyCoordinates", {
                  defaultValue: "Copy coordinates",
                })
            }
            withArrow
            openDelay={clipboard.copied ? 0 : 300}
          >
            <ActionIcon
              variant="subtle"
              size="xs"
              color={clipboard.copied ? "teal" : "gray"}
              onClick={handleCopyCoords}
              aria-label={t("common:location.copyCoordinates", {
                defaultValue: "Copy coordinates",
              })}
              style={{ flexShrink: 0 }}
            >
              <AppIcon
                iconName={clipboard.copied ? "Check" : "Copy"}
                size={14}
              />
            </ActionIcon>
          </Tooltip>

          {/* Open in maps link (only when map image is not shown) */}
          {!showMap && (
            <Tooltip
              label={t("common:location.openInMaps", {
                defaultValue: "Open in Google Maps",
              })}
              withArrow
              openDelay={300}
            >
              <ActionIcon
                variant="subtle"
                size="xs"
                color="gray"
                onClick={handleOpenMaps}
                aria-label={t("common:location.openInMaps", {
                  defaultValue: "Open in Google Maps",
                })}
                style={{ flexShrink: 0 }}
              >
                <AppIcon iconName="ExternalLink" size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      )}
    </Box>
  );
};